import { useState, useEffect, useMemo } from 'react';
import { collection, query, where, orderBy, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from '../services/firebase';
import { Persona } from '../types/persona';

interface UsePersonasReturn {
  personas: Persona[];
  loading: boolean;
  error: Error | null;
  searchPersonas: (searchTerm: string) => Persona[];
  filterByBrandRelationship: (usageStatus: string) => Persona[];
  filterByDemographic: (field: keyof Persona['demographics'], value: string) => Persona[];
  sortPersonas: (field: string, direction: 'asc' | 'desc') => Persona[];
}

/**
 * Custom hook to fetch and subscribe to personas for a project
 * Provides real-time updates and filtering/search functionality
 */
export const usePersonas = (projectId: string | undefined): UsePersonasReturn => {
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Reset state if no projectId
    if (!projectId) {
      setPersonas([]);
      setLoading(false);
      return;
    }

    try {
      // Query personas for the project
      const q = query(
        collection(db, 'personas'),
        where('projectId', '==', projectId),
        orderBy('createdAt', 'desc')
      );

      // Set up real-time listener
      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const personasList: Persona[] = snapshot.docs.map((doc) => {
            const data = doc.data();
            return {
              id: doc.id,
              personaId: doc.id,
              projectId: data.projectId,
              name: data.name,
              avatarUrl: data.avatarUrl, // AI-generated avatar image URL
              demographics: data.demographics,
              cognitive: data.cognitive,
              psychographics: data.psychographics,
              brand_relationship: data.brand_relationship,
              professional_context: data.professional_context,
              narrative_bio: data.narrative_bio,
              createdAt: data.createdAt instanceof Timestamp
                ? data.createdAt.toDate()
                : new Date(data.createdAt),
              updatedAt: data.updatedAt instanceof Timestamp
                ? data.updatedAt.toDate()
                : new Date(data.updatedAt),
            };
          });

          setPersonas(personasList);
          setLoading(false);
          setError(null);
        },
        (err) => {
          console.error('Error fetching personas:', err);
          setError(err instanceof Error ? err : new Error('Failed to fetch personas'));
          setLoading(false);
        }
      );

      // Cleanup listener on unmount
      return () => unsubscribe();
    } catch (err) {
      console.error('Error setting up personas listener:', err);
      setError(err instanceof Error ? err : new Error('Failed to set up personas listener'));
      setLoading(false);
    }
  }, [projectId]);

  // Search function
  const searchPersonas = useMemo(
    () => (searchTerm: string): Persona[] => {
      if (!searchTerm.trim()) return personas;

      const term = searchTerm.toLowerCase();
      return personas.filter(
        (p) =>
          p.name.toLowerCase().includes(term) ||
          p.demographics.occupation.toLowerCase().includes(term) ||
          p.demographics.location.toLowerCase().includes(term) ||
          p.narrative_bio.toLowerCase().includes(term)
      );
    },
    [personas]
  );

  // Filter by brand relationship
  const filterByBrandRelationship = useMemo(
    () => (usageStatus: string): Persona[] => {
      if (!usageStatus) return personas;
      return personas.filter((p) => p.brand_relationship.usage_status === usageStatus);
    },
    [personas]
  );

  // Filter by demographic field
  const filterByDemographic = useMemo(
    () => (field: keyof Persona['demographics'], value: string): Persona[] => {
      if (!value) return personas;
      return personas.filter((p) => {
        const fieldValue = p.demographics[field];
        return typeof fieldValue === 'string' && fieldValue === value;
      });
    },
    [personas]
  );

  // Sort personas
  const sortPersonas = useMemo(
    () => (field: string, direction: 'asc' | 'desc' = 'asc'): Persona[] => {
      const sorted = [...personas].sort((a, b) => {
        let aVal: any;
        let bVal: any;

        // Handle nested fields
        if (field.startsWith('demographics.')) {
          const demoField = field.split('.')[1] as keyof Persona['demographics'];
          aVal = a.demographics[demoField];
          bVal = b.demographics[demoField];
        } else if (field.startsWith('cognitive.')) {
          const cogField = field.split('.')[1] as keyof Persona['cognitive'];
          aVal = a.cognitive[cogField];
          bVal = b.cognitive[cogField];
        } else if (field === 'name') {
          aVal = a.name;
          bVal = b.name;
        } else {
          aVal = a[field as keyof Persona];
          bVal = b[field as keyof Persona];
        }

        // Handle different types
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return direction === 'asc' ? aVal - bVal : bVal - aVal;
        }

        if (typeof aVal === 'string' && typeof bVal === 'string') {
          return direction === 'asc'
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        }

        return 0;
      });

      return sorted;
    },
    [personas]
  );

  return {
    personas,
    loading,
    error,
    searchPersonas,
    filterByBrandRelationship,
    filterByDemographic,
    sortPersonas,
  };
};
